import React, { useCallback, useMemo } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/hooks';
import { openNewWindow } from '@/utils';

import { IconOrgVerified, IconOfficialUnverified, IconOfficialUnverifiedDark } from '@/assets/org';
import HelperTooltip from '../HelperTooltip';
import style from './index.module.css';
import { TooltipAlignConfig, TooltipPlacement } from 'antd/es/tooltip';

export const OrgVerifiedLogo = ({
  size = 22,
  className,
  tooltipClassName,
  disableTooltip,
  marginLeft = 12,
  style: customStyle,
  marginRight = 0,
  unverified = false,
  getTooltipContainer,
  placement = 'bottom',
  align,
}: {
  size?: number;
  disableTooltip?: boolean;
  className?: string;
  tooltipClassName?: string;
  verifiedLink?: string;
  style?: React.CSSProperties;
  marginLeft?: number;
  marginRight?: number;
  unverified?: boolean;
  getTooltipContainer?: (dom: HTMLElement) => HTMLElement;
  placement?: TooltipPlacement;
  align?: TooltipAlignConfig;
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const Logo = useMemo(
    () =>
      unverified ? (isDark ? IconOfficialUnverifiedDark : IconOfficialUnverified) : IconOrgVerified,
    [isDark, unverified],
  );
  const logo = useMemo(
    () => (
      <Logo
        className={cx(style.logo, className)}
        width={size}
        height={size}
        style={{ marginLeft, marginRight, ...customStyle }}
      />
    ),
    [Logo, className, customStyle, marginLeft, marginRight, size],
  );

  const handleOfficialLinkClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    openNewWindow('https://official.debank.com/', '_blank');
  }, []);

  const title = useMemo(
    () => (
      <div className={style.tooltipTitle}>
        <div>{unverified ? t('OrgVerifiedLogo.notverify') : t('OrgVerifiedLogo.verified')}</div>
        <div className={style.link} onClick={handleOfficialLinkClick}>
          {t('OrgVerifiedLogo.applyOfficial')}
        </div>
      </div>
    ),
    [handleOfficialLinkClick, unverified, t],
  );

  return disableTooltip ? (
    logo
  ) : (
    <HelperTooltip
      overlayClassName={tooltipClassName}
      placement={placement}
      overlayStyle={{ maxWidth: 600 }}
      title={title}
      getTooltipContainer={getTooltipContainer}
      align={align}>
      {logo}
    </HelperTooltip>
  );
};
